/**
 * Reusable Select Component
 * Supports dropdown selection with label and error handling.
 *
 * Two ways to supply the choices (both are in use, both must keep working):
 *   - `options={[{ value, label }]}`: rendered as <option> elements, in order;
 *   - `children`: your own <option>/<optgroup> elements, rendered as given.
 * If both are passed, the `options` come first and the children follow.
 *
 * Until 2026-10-16 `options` was not a declared prop: it fell into `...props` and was
 * spread onto the DOM <select> (where it does nothing), so every caller that relied on it
 * (Tasks, Contact organization, QR generator) rendered an EMPTY dropdown. It is now pulled
 * out of the DOM props explicitly — never spread it again.
 */

import type { ReactNode, SelectHTMLAttributes } from "react";
import { forwardRef, useId } from "react";
import clsx from "clsx";

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  helperText?: string;
  fullWidth?: boolean;
  /** The choices, rendered before any children. */
  options?: ReadonlyArray<SelectOption>;
  children?: ReactNode;
}

const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      error,
      helperText,
      fullWidth = false,
      className,
      id,
      options,
      children,
      ...props
    },
    ref,
  ) => {
    const autoId = useId();
    const selectId = id || `select-${autoId}`;
    const hasError = !!error;

    const containerStyles = fullWidth ? "w-full" : "";

    const selectStyles = clsx(
      "block w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-0 transition-colors",
      {
        "border-gray-300 focus:border-blue-500 focus:ring-blue-500": !hasError,
        "border-red-500 focus:border-red-500 focus:ring-red-500": hasError,
        "bg-gray-100 cursor-not-allowed": props.disabled,
      },
      className,
    );

    return (
      <div className={containerStyles}>
        {label && (
          <label
            htmlFor={selectId}
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            {label}
            {props.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        <select
          ref={ref}
          id={selectId}
          className={selectStyles}
          aria-invalid={hasError}
          aria-describedby={
            error
              ? `${selectId}-error`
              : helperText
                ? `${selectId}-helper`
                : undefined
          }
          {...props}
        >
          {options?.map((option, index) => (
            <option
              // Index in the key: two options may legitimately share a value (e.g. a "none" row).
              key={`${index}:${option.value}`}
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </option>
          ))}
          {children}
        </select>
        {error && (
          <p id={`${selectId}-error`} className="mt-1 text-sm text-red-600">
            {error}
          </p>
        )}
        {helperText && !error && (
          <p id={`${selectId}-helper`} className="mt-1 text-sm text-gray-500">
            {helperText}
          </p>
        )}
      </div>
    );
  },
);

Select.displayName = "Select";

export default Select;
